import { isProductionDomain } from './environment';

// API Configuration - Dynamic function to get API base URL
export const getApiBaseUrl = (): string => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  console.log('API Config: hostname =', hostname, 'protocol =', protocol);
  
  // Production domain - always use relative URLs
  if (isProductionDomain()) {
    console.log('API Config: Using production domain with relative URLs');
    return ''; // Use relative URLs for production
  }
  
  // HTTPS access (Cloudflare tunnel or other HTTPS) - always use relative URLs
  if (protocol === 'https:') {
    console.log('API Config: Using relative URLs for HTTPS (tunnel access)');
    return ''; // Use relative URLs to avoid mixed content
  }
  
  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    console.log('API Config: Using localhost:8000');
    return 'http://localhost:8000';
  }
  
  // Local network access (development)
  if (protocol === 'http:') {
    console.log('API Config: Using HTTP with port 8000 for local network');
    return `http://${hostname}:8000`;
  }
  
  // Fallback
  console.log('API Config: Using fallback configuration');
  return process.env.REACT_APP_API_URL || '';
};

// Dynamic API base URL - always call function to get current value
export const getCurrentApiBaseUrl = getApiBaseUrl;

// API Endpoints - Dynamic function to get endpoints
export const getApiEndpoints = () => {
  const baseUrl = getApiBaseUrl();
  return {
    // Auth
    LOGIN: `${baseUrl}/api/auth/login`,
    REGISTER: `${baseUrl}/api/auth/register`,
    FORGOT_PASSWORD: `${baseUrl}/api/auth/forgot-password`,
    VERIFY_RESET_TOKEN: `${baseUrl}/api/auth/verify-reset-token`,
    RESET_PASSWORD: `${baseUrl}/api/auth/reset-password`,
    CREATE_ADMIN: `${baseUrl}/api/auth/create-admin`,
    ME: `${baseUrl}/api/auth/me`,
    
    // Registration
    CREATE_NEW_REGISTRATION: `${baseUrl}/api/create-new-registration`,
    REGISTRATION: `${baseUrl}/api/registration`,
    REGISTRATION_BY_ID: (id: number) => `${baseUrl}/api/registration/${id}`,
    
    // Business Activity
    BUSINESS_ACTIVITIES: `${baseUrl}/api/business-activities`,
    COMPANY_TYPE: `${baseUrl}/api/company-type`,
    BUSINESS_ACTIVITY: `${baseUrl}/api/business-activity`,
    
    // Company Name
    COMPANY_NAME: `${baseUrl}/api/company-name`,
    
    // Owners Info
    OWNERS_INFO: `${baseUrl}/api/owners-info`,
    UPLOAD_DOCUMENT: `${baseUrl}/api/upload-document`,
    
    // Charter
    CHARTER: `${baseUrl}/api/charter`,
    
    // Additional Services
    ADDITIONAL_SERVICES: `${baseUrl}/api/additional-services`,
    
    // Submit
    SUBMIT: `${baseUrl}/api/submit`,
    
    // User Registrations
    MY_REGISTRATIONS: `${baseUrl}/api/my-registrations`,
    MY_REGISTRATION_BY_ID: (id: number) => `${baseUrl}/api/my-registrations/${id}`,
    MARK_PAYMENT_COMPLETE: (id: number) => `${baseUrl}/api/my-registrations/${id}/mark-payment-complete`,
    
    // Admin
    ADMIN_DASHBOARD: `${baseUrl}/api/admin/dashboard`,
    ADMIN_REGISTRATIONS: `${baseUrl}/api/admin/registrations`,
    ADMIN_REGISTRATION_BY_ID: (id: number) => `${baseUrl}/api/admin/registrations/${id}`,
    UPDATE_REGISTRATION_STATUS: (id: number) => `${baseUrl}/api/admin/registrations/${id}/status`,
    UPDATE_PAYMENT_LINK: (id: number) => `${baseUrl}/api/admin/registrations/${id}/payment-link`,
    DELETE_REGISTRATION: (id: number) => `${baseUrl}/api/admin/registrations/${id}`,
    
    // File Operations
    RESOLVE_FILE: (filename: string) => `${baseUrl}/api/resolve-file/${encodeURIComponent(filename)}`,
    DOWNLOAD_FILE: (filename: string) => `${baseUrl}/api/download-file/${encodeURIComponent(filename)}`,
    ADMIN_DOWNLOAD_FILE: (filename: string) => `${baseUrl}/api/admin/download-file/${encodeURIComponent(filename)}`,
    
    // Document Management - Admin
    ADMIN_UPLOAD_DOCUMENT: (id: number) => `${baseUrl}/api/admin/registrations/${id}/upload-document`,
    ADMIN_GET_DOCUMENTS: (id: number) => `${baseUrl}/api/admin/registrations/${id}/documents`,
    ADMIN_DOWNLOAD_DOCUMENT: (id: number, docId: number) => `${baseUrl}/api/admin/registrations/${id}/documents/${docId}/download`,
    ADMIN_APPROVE_DOCUMENT: (id: number, docId: number) => `${baseUrl}/api/admin/registrations/${id}/documents/${docId}/approve`,
    
    // Document Management - User
    USER_GET_DOCUMENTS: (id: number) => `${baseUrl}/api/my-registrations/${id}/documents`,
    USER_DOWNLOAD_DOCUMENT: (id: number, docId: number) => `${baseUrl}/api/my-registrations/${id}/documents/${docId}/download`,
    USER_UPLOAD_SIGNED_DOCUMENT: (id: number, docId: number) => `${baseUrl}/api/my-registrations/${id}/documents/${docId}/upload-signed`,
  };
};

// Dynamic API endpoints - always call function to get current value
export const getCurrentApiEndpoints = getApiEndpoints;